import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create the context
const AIProfileContext = createContext(null);

// Profile Provider component
export const AIProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set profile directly (from quiz completion)
  const setAIProfile = useCallback((newProfile) => {
    setProfile(newProfile);
    // Also store in sessionStorage for persistence during the session
    if (newProfile) {
      sessionStorage.setItem('aiProfile', JSON.stringify(newProfile));
      sessionStorage.setItem('profileId', newProfile.id);
    }
  }, []);

  // Load profile from sessionStorage on mount
  const loadStoredProfile = useCallback(() => {
    const stored = sessionStorage.getItem('aiProfile');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setProfile(parsed);
        return parsed;
      } catch (e) {
        console.error('Failed to parse stored profile:', e);
      }
    }
    return null;
  }, []);

  // Fetch profile by ID from API
  const fetchProfile = useCallback(async (profileId) => {
    if (!profileId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/profiles/${profileId}`);
      setProfile(response.data);
      sessionStorage.setItem('aiProfile', JSON.stringify(response.data));
      sessionStorage.setItem('profileId', profileId);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch profile by lead ID
  const fetchProfileByLead = useCallback(async (leadId) => {
    if (!leadId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/profiles/lead/${leadId}`);
      setProfile(response.data);
      sessionStorage.setItem('aiProfile', JSON.stringify(response.data));
      sessionStorage.setItem('profileId', response.data.id);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch profile by lead:', err);
      setError('Failed to load profile');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear profile (e.g., on logout or new quiz)
  const clearProfile = useCallback(() => {
    setProfile(null);
    setError(null);
    sessionStorage.removeItem('aiProfile');
    sessionStorage.removeItem('profileId');
  }, []);

  // Get current profile ID
  const getProfileId = useCallback(() => {
    return profile?.id || sessionStorage.getItem('profileId') || null;
  }, [profile]);

  // Check if user has a profile
  const hasProfile = useCallback(() => {
    return !!profile || !!sessionStorage.getItem('profileId');
  }, [profile]);

  // Get profile summary for display
  const getProfileSummary = useCallback(() => {
    if (!profile) return null;
    
    return {
      name: profile.first_name,
      weightGoal: `${profile.current_weight} → ${profile.goal_weight} lbs`,
      weightToLose: `${profile.weight_to_lose} lbs`,
      timeline: profile.timeline,
      readiness: profile.readiness_score,
      recommendedMed: profile.recommended_medication,
      approach: profile.personalized_approach,
      motivators: profile.key_motivators,
      obstacles: profile.potential_obstacles
    };
  }, [profile]);

  const value = {
    profile,
    loading,
    error,
    setAIProfile,
    loadStoredProfile,
    fetchProfile,
    fetchProfileByLead,
    clearProfile,
    getProfileId,
    hasProfile,
    getProfileSummary
  };

  return (
    <AIProfileContext.Provider value={value}>
      {children}
    </AIProfileContext.Provider>
  );
};

// Custom hook to use the profile context
export const useAIProfile = () => {
  const context = useContext(AIProfileContext);
  if (!context) {
    throw new Error('useAIProfile must be used within an AIProfileProvider');
  }
  return context;
};

export default AIProfileContext;
